<template>
    <div class="snowflake-board">
        <header class="board-header">
            <p v-if="loadError" class="board-status board-status--error">{{ loadError }}</p>
            <p v-else-if="loading" class="board-status">Loading...</p>
            <p v-else>For Yuletide 2026, AO3 gave to me: 4-2 weeks of downtime aaaand <strong> {{ cells.length }}/{{
                totalActive
                    }}</strong> Yulegoat <a href="https://yuletide.dreamwidth.org/374720.html">pledges</a> to create
                for
                <a href="https://yuletide.dreamwidth.org/tag/mini-challenge" target="_blank">mini-challenges</a>...
            </p>
        </header>

        <!-- The grid. Every slot is a grid item; only snowflake slots render a button. -->
        <div class="grid" :style="gridStyle">
            <div v-for="slot in slots" :key="slot.id" class="slot" :class="tooltipPosition(slot)">
                <button v-if="slot.active" type="button" class="cell" :class="['btn', { 'cell--done': !!slot.record }]"
                    :style="slot.record ? { backgroundColor: slot.record.color } : null" :disabled="loading"
                    :aria-label="cellLabel(slot)" @click="handleCellClick(slot)">
                    <span v-if="slot.record" class="cell__x" aria-hidden="true">✕</span>
                </button>

                <!-- Sibling of the button (not a child) so the button's overflow: hidden doesn't clip it -->
                <span v-if="slot.record" class="tooltip" role="tooltip" aria-hidden="true">
                    <span class="tooltip__row">
                        <span class="tooltip__label">{{ dropdownOneLabel }}</span>
                        {{ slot.record.minichallenge }}
                    </span>
                    <span class="tooltip__row">
                        <span class="tooltip__label">{{ dropdownTwoLabel }}</span>
                        {{ slot.record.fandom }}
                    </span>
                </span>
            </div>
        </div>

        <!-- Modals -->
        <transition name="modal">
            <div v-if="modal.type" class="backdrop" @mousedown.self="closeModal">
                <!-- Create modal -->
                <div v-if="modal.type === 'create'" class="modal" role="dialog" aria-modal="true"
                    aria-labelledby="modal-title">
                    <h2 id="modal-title">Dear Yulegoat, I promise to make...</h2>
                    <!-- <p class="modal__sub">Row {{ modal.row + 1 }}, column {{ modal.col + 1 }}</p> -->

                    <label class="mt-1 form-group ">
                        <span class="form-label">Mini-challenge</span>
                        <select class="form-select" ref="firstSelect" v-model="minichallenge" :disabled="saving">
                            <option value="" disabled>Select...</option>
                            <option v-for="opt in minichallenges" :key="opt" :value="opt">{{ opt }}</option>
                        </select>
                    </label>

                    <label class="">
                        <span class="form-label">Fandom</span>
                        <fandom-autocomplete class="autocomplete" :fandoms="fandoms" :show-label="false"
                            @update="setFandom($event)"></fandom-autocomplete>
                    </label>
                    <p class="selected-fandom"><strong>{{ fandom }}</strong></p>

                    <p v-if="saveError" class="modal__error" role="alert">{{ saveError }}</p>

                    <div class="modal__actions">
                        <button type="button" class="btn" :disabled="saving" @click="closeModal">
                            Cancel
                        </button>
                        <button type="button" class="btn btn-primary" :disabled="!canSubmit || saving"
                            @click="submitSelection">
                            {{ saving ? 'Saving…' : 'I do!' }}
                        </button>
                    </div>
                </div>

                <!-- Details modal -->
                <div v-else-if="modal.type === 'details' && activeRecord" class="modal" role="dialog" aria-modal="true"
                    aria-labelledby="modal-title">
                    <!-- <h2 id="modal-title">Cell details</h2> -->
                    <!-- <p class="modal__sub">Row {{ modal.row + 1 }}, column {{ modal.col + 1 }}</p> -->

                    <dl class="details">
                        <div>
                            <dt>{{ dropdownOneLabel }}</dt>
                            <dd>{{ activeRecord.minichallenge }}</dd>
                        </div>
                        <div>
                            <dt>{{ dropdownTwoLabel }}</dt>
                            <dd>{{ activeRecord.fandom }}</dd>
                        </div>
                    </dl>

                    <p v-if="confirmingDelete" class="modal__warn" role="alert">
                        You're going to break this promise! Don't break promises that aren't yours :( But if you must,
                        click delete again.
                    </p>
                    <p v-if="saveError" class="modal__error" role="alert">{{ saveError }}</p>

                    <div class="modal__actions">
                        <button v-if="!confirmingDelete" type="button" class="btn" @click="closeModal">
                            Close
                        </button>
                        <button v-else type="button" class="btn" :disabled="saving" @click="confirmingDelete = false">
                            Cancel
                        </button>

                        <button type="button" class="btn btn--danger" :disabled="saving" @click="handleDeleteClick">
                            <template v-if="saving">Deleting…</template>
                            <template v-else-if="confirmingDelete">DELETE</template>
                            <template v-else>Delete</template>
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <div class="footnote"><small>* Yes, it's an honour system; yes, you should probably go find prompts by someone
                who's
                posted
                in
                each
                of the
                mini-challenge posts first... and add yourselves to those... but just tagging any treat when appropriate
                is
                totally cool too. The colours are randomised by square, not by mini-challenge! Do your best for your
                pledge, but
                come back and delete your square if you need to back out. Also, please appreciate this being in Verdana
                like
                we're on
                yuletidetreasure.org and it's 2012...</small></div>
    </div>
</template>

<script>
/**
 * Snowflake layout. '#' = clickable cell, '.' = empty space.
 * 19 x 19 grid, 101 clickable cells. Edit the strings to reshape the flake.
 */
const SNOWFLAKE_MASK = [
    '.#.......#.......#.',
    '........###........',
    '.........#.........',
    '...##..#.#.#..##...',
    '...##...###...##...',
    '.....#...#...#.....',
    '......#.###.#......',
    '...#...#.#.#...#...',
    '.#..#.#.###.#.#..#.',
    '###################',
    '.#..#.#.###.#.#..#.',
    '...#...#.#.#...#...',
    '......#.###.#......',
    '.....#...#...#.....',
    '...##...###...##...',
    '...##..#.#.#..##...',
    '.........#.........',
    '........###........',
    '.#.......#.......#.'
]

const STUB_FIREBASE_URL = 'https://tagset2026-default-rtdb.firebaseio.com'
const SNOWFLAKE_ENDPOINT = `${STUB_FIREBASE_URL}/snowflake.json`
import { mapGetters } from "vuex";
import FandomAutocomplete from "../components/minichallenge-fandom-autocomplete.vue";


const FILL_COLOURS = [
    '#c8102e', '#a4122b', '#7a1530', '#e03a3e', '#d94f6a', '#8c2f39',
    '#1f7a3d', '#0f5132', '#4c9a5b', '#2e8b6f', '#7fb069', '#a3b86c', '#0b6e4f',
    '#e0a526', '#f2c14e', '#d98324', '#b8732f', '#9c5a2b', '#c9a24d',
    '#5fa8d3', '#3b6ea8', '#1f3a6e', '#6b4c9a', '#8e5ea2',
    '#6e1a2e', '#f08cab', '#e0661c', '#8b5a2b', '#3fa34d',
    '#c6df7c', '#2a9d8f', '#6ab7e8', '#2f5bd3', '#8e44ad'
]

function randomColour() {
    return FILL_COLOURS[Math.floor(Math.random() * FILL_COLOURS.length)]
}

export default {
    name: 'snowflake',
    components: {
        FandomAutocomplete
    },
    data() {
        return {
            mask: SNOWFLAKE_MASK,

            // Stubbed dropdown config
            dropdownOneLabel: 'Mini-challenge',
            dropdownTwoLabel: 'Fandom',
            minichallenges: ['Chromatic Yuletide', 'Crueltide', 'Family Matters', 'Femslash Festivus', 'Interactive Fiction (IF)', 'Misses Clause', 'MultiLingYule', 'The Yuletide Madness Drabble Invitational', 'NeuroTide', 'Season\'s Treating', 'Three Turtle Doves', 'TransTide', 'Two for One', ' Unconventionyule', 'Wrapping Paper', 'Yulebuilding', 'Yulepoem', 'Yuleporn', 'Yulternatives', 'Yuletide Takes the Case'],
            fandom: '',
            minichallenge: '',

            cells: [],
            loading: true,
            loadError: '',

            modal: { type: null, id: null, row: null, col: null }, // type: 'create' | 'details' | null
            form: { one: '', two: '' },
            saving: false,
            saveError: '',
            confirmingDelete: false
        }
    },

    computed: {
        ...mapGetters(["fandoms"]),
        columns() {
            return this.mask[0].length
        },
        totalActive() {
            return this.mask.join('').split('#').length - 1
        },
        gridStyle() {
            return { gridTemplateColumns: `repeat(${this.columns}, var(--cell-size))` }
        },
        recordsById() {
            const map = {}
            this.cells.forEach(c => { map[c.id] = c })
            return map
        },
        slots() {
            const out = []
            this.mask.forEach((rowStr, row) => {
                rowStr.split('').forEach((ch, col) => {
                    const id = `r${row}c${col}`
                    out.push({ id, row, col, active: ch === '#', record: this.recordsById[id] || null })
                })
            })
            return out
        },
        canSubmit() {
            return this.fandom.length && this.minichallenge.length
        },
        activeRecord() {
            return this.modal.id ? this.recordsById[this.modal.id] : null
        }
    },

    created() {
        this.loadCells()
    },

    mounted() {
        window.addEventListener('keydown', this.onKeydown)
    },

    beforeDestroy() {
        window.removeEventListener('keydown', this.onKeydown)
    },

    methods: {
        /* ---------- Firebase REST layer ----------
         * Data is stored at /snowflake as an object keyed by cell id:
         *   { "r3c9": { id, row, col, minichallenge, fandom, color }, ... }
         * All three calls hit SNOWFLAKE_ENDPOINT (…/snowflake.json):
         *   GET   -> read every cell (returns null when empty)
         *   PATCH -> merge { [id]: record } to create a cell
         *   PATCH -> merge { [id]: null } to delete a cell (Firebase removes null keys)
         */
        async firebaseRequest(method, body) {
            const options = { method, headers: { 'Content-Type': 'application/json' } }
            if (body !== undefined) options.body = JSON.stringify(body)

            const res = await fetch(SNOWFLAKE_ENDPOINT, options)
            if (!res.ok) {
                throw new Error(`Firebase ${method} failed with status ${res.status}`)
            }
            return res.json()
        },

        async fetchCellsFromDb() {
            const data = await this.firebaseRequest('GET')
            return data ? Object.values(data) : []
        },

        async saveCellToDb(record) {
            await this.firebaseRequest('PATCH', { [record.id]: record })
            return record
        },

        async deleteCellFromDb(id) {
            await this.firebaseRequest('PATCH', { [id]: null })
            return id
        },

        /* ---------- Loading ---------- */
        async loadCells() {
            this.loading = true
            this.loadError = ''
            try {
                this.cells = await this.fetchCellsFromDb()
            } catch (err) {
                this.loadError = 'Couldn’t load cells. Refresh to try again.'
            } finally {
                this.loading = false
            }
        },

        /* ---------- Click handling ---------- */
        handleCellClick(slot) {
            if (this.saving) return
            this.fandom = '';
            this.minichallenge = '';
            this.saveError = ''
            this.confirmingDelete = false

            const type = slot.record ? 'details' : 'create'
            this.modal = { type, id: slot.id, row: slot.row, col: slot.col }

            if (type === 'create') {
                this.$nextTick(() => {
                    if (this.$refs.firstSelect) this.$refs.firstSelect.focus()
                })
            }
        },

        async submitSelection() {
            if (!this.canSubmit || this.saving) return
            this.saving = true
            this.saveError = ''

            const record = {
                id: this.modal.id,
                row: this.modal.row,
                col: this.modal.col,
                minichallenge: this.minichallenge,
                fandom: this.fandom,
                color: randomColour()
            }

            try {
                const saved = await this.saveCellToDb(record)
                this.cells.push(saved)
                this.closeModal(true)
            } catch (err) {
                this.saveError = 'Couldn’t save this cell. Try again.'
            } finally {
                this.saving = false
            }
        },

        async handleDeleteClick() {
            if (this.saving) return

            // First click arms the button, second click deletes.
            if (!this.confirmingDelete) {
                this.confirmingDelete = true
                return
            }

            this.saving = true
            this.saveError = ''
            try {
                const id = this.modal.id
                await this.deleteCellFromDb(id)
                this.cells = this.cells.filter(c => c.id !== id)
                this.closeModal(true)
            } catch (err) {
                this.saveError = 'Couldn’t delete this cell. Try again.'
            } finally {
                this.saving = false
            }
        },

        /* ---------- Helpers ---------- */
        closeModal(force) {
            if (this.saving && force !== true) return
            this.modal = { type: null, id: null, row: null, col: null }
            this.confirmingDelete = false
            this.saveError = ''
        },

        onKeydown(e) {
            if (e.key === 'Escape' && this.modal.type) this.closeModal()
        },

        cellLabel(slot) {
            const where = `Row ${slot.row + 1}, column ${slot.col + 1}`
            if (!slot.record) return `${where}, empty. Fill cell`
            const { minichallenge, fandom } = slot.record
            return `${where}, filled: ${minichallenge}, ${fandom}. View details`
        },

        // Keeps tooltips near the left/right edges from running off-screen
        tooltipPosition(slot) {
            if (slot.col < 4) return 'slot--tip-left'
            if (slot.col > this.columns - 5) return 'slot--tip-right'
            return null
        },

        setFandom(data) {
            this.fandom = data.fandom.name;
        }
    }
}
</script>

<style lang="scss" scoped>
.snowflake-board {
    font-size: 13px;
    --cell-size: clamp(13px, 4.2vw, 30px);
    --cell-gap: 3px;
    --cell-grey: #d8dbe0;

    min-height: 100vh;
    box-sizing: border-box;
    padding: 32px 16px 48px;
    background: #2d252d;
    color: #eef2fb;
    font-family: 'verdana', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.footnote {
    width: 80%;
    margin: 30px auto;
    text-align: center;
}

.board-header {
    text-align: center;
    margin-bottom: 28px;
}

.board-status {
    margin: 0;
    // font-size: 0.95rem;
    color: #aebbdc;
}

.board-status--error {
    color: #ffb4b4;
}

/* Grid */
.grid {
    display: grid;
    gap: var(--cell-gap);
    grid-auto-rows: var(--cell-size);
}

.slot {
    position: relative;
    width: var(--cell-size);
    height: var(--cell-size);
}

.slot:hover,
.slot:focus-within {
    z-index: 10;
}

/* Tooltip */
.tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: max-content;
    max-width: 220px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #f6f8fc;
    color: #1a2340;
    // font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.25;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
    pointer-events: none;
    opacity: 0;
    transform: translateX(-50%) translateY(4px);
    transition: opacity 0.12s ease, transform 0.12s ease;
}

.tooltip__row {
    display: block;
}

.tooltip__label {
    display: block;
    // font-size: 0.7rem;
    font-weight: 500;
    color: #5b6685;
}

.slot:hover .tooltip,
.slot:focus-within .tooltip {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
}

.slot--tip-left .tooltip {
    left: 0;
    transform: translateY(4px);
}

.slot--tip-right .tooltip {
    left: auto;
    right: 0;
    transform: translateY(4px);
}

.slot--tip-left:hover .tooltip,
.slot--tip-left:focus-within .tooltip,
.slot--tip-right:hover .tooltip,
.slot--tip-right:focus-within .tooltip {
    transform: translateY(0);
}

.cell {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background-color: var(--cell-grey);
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.12s ease, filter 0.12s ease;
}

.cell:hover:not(:disabled) {
    transform: scale(1.12);
    z-index: 1;
}

.cell:disabled {
    cursor: progress;
    opacity: 0.6;
}

.cell:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
}

.cell--done::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(10, 18, 40, 0);
    transition: background 0.12s ease;
}

.cell--done:hover::after,
.cell--done:focus-visible::after {
    background: rgba(10, 18, 40, 0.35);
}

.cell__x {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: calc(var(--cell-size) * 0.5);
    font-weight: 700;
    line-height: 1;
    color: #ffffff;
    opacity: 0;
    transition: opacity 0.12s ease;
}

.cell--done:hover .cell__x,
.cell--done:focus-visible .cell__x {
    opacity: 1;
}

/* Modal */
.backdrop {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(8, 14, 32, 0.65);
}

.modal {
    width: 100%;
    height: auto;
    max-width: 380px;
    box-sizing: border-box;
    padding: 24px;
    border-radius: 12px;
    background: #f6f8fc;
    color: #1a2340;
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.4);
}

#modal-title {
    margin: 0;
    font-size: 1.1em;
    font-weight: 600;
}

.modal__sub {
    margin: 4px 0 20px;
    font-size: 0.9rem;
    color: #5b6685;
}

.field {
    display: block;
    margin-bottom: 16px;
}

.field__label {
    display: block;
    margin-bottom: 6px;
    font-size: 0.9rem;
    font-weight: 600;
}

.autocomplete {
    position: relative;
    top: 0;
}

.selected-fandom,
.form-select {
    font-size: 1em;
}

.details {
    margin: 0 0 20px;
}

.details>div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #dde3f0;
}

.details dt {
    // font-size: 0.9rem;
    color: #5b6685;
}

.details dd {
    margin: 0;
    padding-left: 10px;
    // font-weight: 600;
}

.swatch {
    display: inline-block;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    vertical-align: middle;
}

.modal__warn {
    font-weight: bold;
    text-align: center;
    margin: 0 0 12px;
    color: #eb3333;
}

.modal__error {
    margin: 0 0 12px;
    font-size: 0.9rem;
    color: #b3261e;
}

.modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}


.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn:focus-visible {
    outline: 2px solid #3b5bdb;
    outline-offset: 2px;
}

.btn--primary {
    background: #3b5bdb;
    color: #ffffff;
}

.btn--ghost {
    background: transparent;
    border-color: #b9c2d8;
    color: #1a2340;
}

.btn--danger {
    background: #c62828;
    color: #ffffff;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.15s ease;
}

.modal-enter-active .modal,
.modal-leave-active .modal {
    transition: transform 0.15s ease;
}

.modal-enter,
.modal-leave-to {
    opacity: 0;
}

.modal-enter .modal,
.modal-leave-to .modal {
    transform: translateY(8px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {

    .cell,
    .cell--done::after,
    .cell__x,
    .tooltip,
    .modal-enter-active,
    .modal-leave-active,
    .modal-enter-active .modal,
    .modal-leave-active .modal {
        transition: none;
    }

    .cell:hover:not(:disabled) {
        transform: none;
    }
}
</style>